/**
 * Deleting a member's outfits: one at a time, a ticked selection, or all of
 * them.
 *
 * The backend is adding these endpoints, so the generated client may not have
 * them yet. They are looked up by name at runtime, and every control here
 * renders nothing until the client carries the operation. When the client is
 * regenerated the controls appear with no further change.
 */

import { useState } from 'react';
import * as api from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText } from '@ui/console/primitives';
import { formatCount } from '@ui/console/measures';
import { ActionDialog } from '@ui/console/PageShell';
import { TextAreaField, TextField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Actions } from './components';
import { errorDetail } from './operation-policy';

/**
 * The one place that knows what the generated client calls the two operations.
 * A generated name ends in its path and method, so the suffix identifies
 * `DELETE /api/v1/internal/users/{id}/outfits/{outfit_id}` and
 * `POST /api/v1/internal/users/{id}/outfits/bulk` whatever the prefix becomes.
 */
export const OUTFIT_DELETE_OPERATION = /ApiV1InternalUsersIdOutfitsOutfitIdDelete$/;
export const OUTFIT_BULK_OPERATION = /ApiV1InternalUsersIdOutfitsBulkPost$/;

type Reply<T> = { data?: T | undefined; error?: unknown; response: { status: number } };

type DeleteOutfitOperation = (options: {
  path: { id: string; outfit_id: string };
  body: { reason: string };
}) => Promise<Reply<unknown>>;

type BulkBody = { verb: 'delete'; reason: string } & ({ outfit_ids: string[] } | { all: true });

export type BulkOutfitOutcome = { outfit_id: string; ok: boolean; error_code?: string | null };

type BulkReply = {
  results?: BulkOutfitOutcome[];
  deleted?: number;
  remaining?: number;
  [key: string]: unknown;
};

type BulkOutfitOperation = (options: {
  path: { id: string };
  body: BulkBody;
}) => Promise<Reply<BulkReply>>;

function findOperation(pattern: RegExp): unknown {
  return Object.entries(api as Record<string, unknown>).find(
    ([name, value]) => pattern.test(name) && typeof value === 'function',
  )?.[1];
}

export function outfitDeletionOperations() {
  return {
    single: findOperation(OUTFIT_DELETE_OPERATION) as DeleteOutfitOperation | undefined,
    bulk: findOperation(OUTFIT_BULK_OPERATION) as BulkOutfitOperation | undefined,
  };
}

/** The per-outfit list, whatever the reply calls it. */
function outcomesOf(reply: BulkReply): BulkOutfitOutcome[] {
  if (Array.isArray(reply.results)) return reply.results;
  for (const value of Object.values(reply)) {
    if (
      Array.isArray(value) &&
      value.every((item) => item && typeof item === 'object' && 'outfit_id' in item)
    )
      return value as BulkOutfitOutcome[];
  }
  return [];
}

/** A refused request, in words a member of staff can act on. */
function refusal(status: number, error: unknown, many: boolean): string {
  if (status === 403)
    return 'Your role isn’t allowed to delete a member’s outfits. Ask an admin if you need this.';
  if (status === 404)
    return many
      ? 'This member, or the outfits you picked, could not be found. They may already have been deleted.'
      : 'This outfit could not be found. It may already have been deleted.';
  if (status === 422)
    return `The request was not accepted: ${errorDetail(error, 'something in it was missing or invalid')}. Check the reason and try again.`;
  return errorDetail(
    error,
    many ? 'The outfits could not be deleted.' : 'The outfit could not be deleted.',
  );
}

const OUTCOME_REASON: Record<string, string> = {
  not_found: 'It was already gone.',
  outfit_not_found: 'It was already gone.',
  forbidden: 'Your role isn’t allowed to delete it.',
  conflict: 'It changed while this was running. Try again.',
};

function outcomeReason(code: string | null | undefined) {
  if (!code) return 'It could not be deleted.';
  return `${OUTCOME_REASON[code] ?? 'It could not be deleted.'} (${code})`;
}

function ReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <TextAreaField
      label="Reason"
      hint="Required. Recorded with your name in the audit log."
      value={value}
      rows={3}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/* ------------------------------------------------------------ one outfit */

export function DeleteOutfitAction({
  memberId,
  outfitId,
  title,
  onDeleted,
}: {
  memberId: string;
  outfitId: string;
  title: string;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const operation = outfitDeletionOperations().single;
  if (!operation) return null;

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    try {
      configureAdminApi();
      const result = await operation({
        path: { id: memberId, outfit_id: outfitId },
        body: { reason: reason.trim() },
      });
      handleUnauthorized(result.response.status);
      if (result.error || result.response.status >= 300) {
        setFailure(refusal(result.response.status, result.error, false));
        return;
      }
      setOpen(false);
      onDeleted();
    } catch (cause) {
      setFailure(errorDetail(cause, 'The outfit could not be deleted. Check your connection.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        color="secondary-destructive"
        onClick={() => {
          setReason('');
          setFailure(undefined);
          setOpen(true);
        }}
      >
        Delete outfit
      </Button>
      {open ? (
        <ActionDialog
          title={`Delete “${title}”?`}
          description="The outfit disappears from the member’s app. This can’t be undone from the admin console."
          onClose={() => setOpen(false)}
        >
          <div className="flex flex-col gap-4">
            {failure ? (
              <Callout tone="error" title="The outfit was not deleted.">
                <FailureText raw={failure} />
              </Callout>
            ) : null}
            <ReasonField value={reason} onChange={setReason} />
            <Actions>
              <Button size="sm" color="secondary" isDisabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary-destructive"
                isDisabled={busy || !reason.trim()}
                onClick={() => void run()}
              >
                {busy ? 'Deleting…' : 'Delete outfit'}
              </Button>
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------ many outfits */

type BulkMode = 'selected' | 'all';

type BulkReport = {
  deleted: number;
  failed: BulkOutfitOutcome[];
  /** Outfits the server still holds when a delete-all stopped making progress. */
  leftOver: number;
};

/** A refusal already worded for the operator. */
class Refused extends Error {}

/** Enough calls to clear any plausible wardrobe, and a hard stop if one never drains. */
const MAX_BULK_CALLS = 500;

/** `BULK_OUTFIT_LIMIT` upstream: the most ids one bulk request may name. */
export const MAX_IDS_PER_CALL = 500;

export function BulkOutfitDeletion({
  memberId,
  memberName,
  selectedIds,
  total,
  titles,
  onClearSelection,
  onDeleted,
}: {
  memberId: string;
  memberName?: string | null | undefined;
  selectedIds: readonly string[];
  /** Every outfit on record for the member, not just the ones on screen. */
  total: number;
  titles: ReadonlyMap<string, string>;
  onClearSelection: () => void;
  onDeleted: () => void;
}) {
  const [mode, setMode] = useState<BulkMode>();
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ deleted: number; remaining: number }>();
  const [failure, setFailure] = useState<string>();
  const [report, setReport] = useState<BulkReport>();
  const operation = outfitDeletionOperations().bulk;
  if (!operation) return null;

  const count = mode === 'all' ? total : selectedIds.length;
  const name = memberName?.trim();
  const typedOk =
    mode !== 'all' ||
    typed.trim() === 'DELETE' ||
    (Boolean(name) && typed.trim().toLowerCase() === name!.toLowerCase());
  const ready = Boolean(reason.trim()) && typedOk && count > 0;

  const start = (next: BulkMode) => {
    setMode(next);
    setReason('');
    setTyped('');
    setFailure(undefined);
    setReport(undefined);
    setProgress(undefined);
  };

  const close = () => {
    const finished = Boolean(report);
    setMode(undefined);
    setReport(undefined);
    if (finished) onClearSelection();
  };

  const call = async (body: BulkBody) => {
    configureAdminApi();
    const result = await operation({ path: { id: memberId }, body });
    handleUnauthorized(result.response.status);
    if (result.error || result.response.status >= 300 || !result.data) {
      throw new Refused(refusal(result.response.status, result.error, true));
    }
    return result.data;
  };

  const run = async () => {
    if (!mode) return;
    setBusy(true);
    setFailure(undefined);
    const text = reason.trim();
    let deleted = 0;
    const failed = new Map<string, BulkOutfitOutcome>();
    let leftOver = 0;
    try {
      if (mode === 'all') {
        for (let calls = 0; calls < MAX_BULK_CALLS; calls += 1) {
          const reply = await call({ verb: 'delete', all: true, reason: text });
          const outcomes = outcomesOf(reply);
          const deletedNow = reply.deleted ?? outcomes.filter((item) => item.ok).length;
          deleted += deletedNow;
          for (const item of outcomes) {
            if (item.ok) failed.delete(item.outfit_id);
            else failed.set(item.outfit_id, item);
          }
          const remaining = reply.remaining ?? 0;
          setProgress({ deleted, remaining });
          if (remaining <= 0) break;
          // Nothing moved this time, so another call would only repeat itself.
          if (deletedNow === 0) {
            leftOver = Math.max(0, remaining - failed.size);
            break;
          }
        }
      } else {
        let pending = [...selectedIds];
        for (let calls = 0; pending.length && calls < MAX_BULK_CALLS; calls += 1) {
          const batch = pending.slice(0, MAX_IDS_PER_CALL);
          const reply = await call({ verb: 'delete', outfit_ids: batch, reason: text });
          const outcomes = outcomesOf(reply);
          deleted += reply.deleted ?? outcomes.filter((item) => item.ok).length;
          for (const item of outcomes) if (!item.ok) failed.set(item.outfit_id, item);
          const answered = new Set(outcomes.map((item) => item.outfit_id));
          setProgress({ deleted, remaining: pending.length - answered.size });
          // A batch the server answered nothing about would be sent again forever.
          if (!answered.size) {
            leftOver = pending.length;
            break;
          }
          pending = pending.filter((id) => !answered.has(id));
        }
      }
      setReport({ deleted, failed: [...failed.values()], leftOver });
    } catch (cause) {
      setFailure(
        cause instanceof Refused
          ? cause.message
          : errorDetail(cause, 'The outfits could not be deleted. Check your connection.'),
      );
      // Whatever went before the refusal did happen, so the report still counts it.
      if (deleted) setReport({ deleted, failed: [...failed.values()], leftOver });
    } finally {
      setBusy(false);
      if (deleted) onDeleted();
    }
  };

  const noun = (n: number) => (n === 1 ? 'outfit' : 'outfits');

  return (
    <>
      <Actions>
        {selectedIds.length ? (
          <Button size="sm" color="secondary-destructive" onClick={() => start('selected')}>
            {`Delete selected (${formatCount(selectedIds.length)})`}
          </Button>
        ) : null}
        {total > 0 ? (
          <Button size="sm" color="secondary-destructive" onClick={() => start('all')}>
            Delete all outfits
          </Button>
        ) : null}
      </Actions>
      {mode ? (
        <ActionDialog
          title={
            mode === 'all'
              ? `Delete all ${formatCount(count)} ${noun(count)}?`
              : `Delete ${formatCount(count)} selected ${noun(count)}?`
          }
          description={
            mode === 'all'
              ? `Every outfit this member has — ${formatCount(count)} in total, including ones not shown on this page — disappears from their app. This can’t be undone from the admin console.`
              : 'The ticked outfits disappear from the member’s app. This can’t be undone from the admin console.'
          }
          width="lg"
          onClose={() => (busy ? undefined : close())}
        >
          <div className="flex flex-col gap-4">
            {failure ? (
              <Callout tone="error" title="Deleting stopped.">
                <FailureText raw={failure} />
              </Callout>
            ) : null}
            {busy && progress ? (
              <p className="text-sm text-secondary" aria-live="polite">
                {`Deleted ${formatCount(progress.deleted)} so far${progress.remaining ? `, ${formatCount(progress.remaining)} still to go` : ''}…`}
              </p>
            ) : busy ? (
              <p className="text-sm text-secondary" aria-live="polite">
                Deleting…
              </p>
            ) : null}
            {report ? (
              <Callout
                tone={report.failed.length || report.leftOver ? 'warning' : 'success'}
                title={`${formatCount(report.deleted)} ${noun(report.deleted)} deleted.`}
              >
                {report.failed.length ? (
                  <div className="flex flex-col gap-2">
                    <span>
                      {`${formatCount(report.failed.length)} ${noun(report.failed.length)} could not be deleted and ${report.failed.length === 1 ? 'is' : 'are'} still there:`}
                    </span>
                    <ul className="flex flex-col gap-1">
                      {report.failed.map((item) => (
                        <li key={item.outfit_id} className="text-sm">
                          <span className="font-medium">
                            {titles.get(item.outfit_id) ?? 'An outfit not on this page'}
                          </span>
                          {' — '}
                          {outcomeReason(item.error_code)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {report.leftOver ? (
                  <span className="block">
                    {`${formatCount(report.leftOver)} ${noun(report.leftOver)} were left in place because deleting stopped making progress. Try again, or ask an engineer to look.`}
                  </span>
                ) : null}
                {!report.failed.length && !report.leftOver ? (
                  <span>Every outfit you asked for was deleted.</span>
                ) : null}
              </Callout>
            ) : null}
            {report ? (
              <Actions>
                <Button size="sm" color="primary" onClick={close}>
                  Done
                </Button>
              </Actions>
            ) : (
              <>
                <ReasonField value={reason} onChange={setReason} />
                {mode === 'all' ? (
                  <TextField
                    label={
                      name
                        ? `Type the member’s name (${name}) or DELETE to confirm`
                        : 'Type DELETE to confirm'
                    }
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                  />
                ) : null}
                <Actions>
                  <Button size="sm" color="secondary" isDisabled={busy} onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    color="primary-destructive"
                    isDisabled={busy || !ready}
                    onClick={() => void run()}
                  >
                    {busy
                      ? 'Deleting…'
                      : mode === 'all'
                        ? `Delete all ${formatCount(count)} ${noun(count)}`
                        : `Delete ${formatCount(count)} ${noun(count)}`}
                  </Button>
                </Actions>
              </>
            )}
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}
